import {
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useMangoApiClient } from '~/atoms/MangoApi'
import { QueryCancellation, UseQueryParameters } from '~/types/ApiRequestTypes'
import { getOrderingString } from '~/utils/apiQueryUtils'

import {
  ApiError,
  Contact,
  ContactSource,
  ContactSourcesQueryParams,
  ContactsQueryParams,
  PaginatedApiResult,
} from '@mangovoice/types'

interface UseGetContactSourcesParams {
  pbxId: number
  onQuerySuccess?: (data: PaginatedApiResult<ContactSource>) => void
  onQueryError?: (queryParams: ContactsQueryParams, error?: ApiError) => void
  options?: Omit<
    UseQueryOptions<PaginatedApiResult<ContactSource>, ApiError>,
    'queryKey'
  >
}

interface UseGetContactSourcesQueryParams {
  search?: string
  offset?: number
  limit?: number
  ordering?: OrderingField<ContactSource>
}

/**
 * @description Get contact sources for a pbx - disabled by default - use refetch with params or set enabled to true in options
 * @param params - pbxId, onQuerySuccess, onQueryError, options
 * @returns
 */
export const useGetContactSources = (params: UseGetContactSourcesParams) => {
  const api = useMangoApiClient()
  const { pbxId, onQuerySuccess, onQueryError, options } = params
  const [queryParams, setQueryParams] =
    useState<UseGetContactSourcesQueryParams>()

  const refetch = (params: UseGetContactSourcesQueryParams) => {
    setQueryParams(params)
  }

  const query = useQuery<PaginatedApiResult<ContactSource>, ApiError>({
    queryKey: ['contactSources', pbxId, { ...queryParams }],
    queryFn: async () => {
      if (pbxId === 0) {
        return Promise.reject({ message: 'pbxId is required' })
      }

      const params: ContactSourcesQueryParams = {
        pbx_id: pbxId,
        search: queryParams?.search,
        offset: queryParams?.offset,
        limit: queryParams?.limit,
        ordering: queryParams?.ordering,
      }

      const { ok, data, status } = await api.contacts.getContactSources(params)

      if (!ok) {
        const error = {
          ...(data as object),
          status,
        }
        onQueryError?.(params, error)
        return Promise.reject(error)
      }

      onQuerySuccess?.(data!)
      return data!
    },
    ...options,
    enabled: options?.enabled ?? false,
    retry: (options?.retry as number | boolean | undefined) ?? false,
  })

  useEffect(() => {
    if (queryParams) {
      query.refetch()
    }
  }, [queryParams])

  return {
    ...query,
    refetch,
  }
}

interface UseGetContactsParams extends UseQueryParameters<
  PaginatedApiResult<Contact>,
  ContactsQueryParams
> {
  pbxId: number
}

interface UseGetContactsQueryParams {
  contactSourceIds?: number[]
  search?: string
  offset?: number
  limit?: number
  ordering?: OrderingField<Contact>[] | OrderingField<Contact>
}

export const useGetContacts = (params: UseGetContactsParams) => {
  const api = useMangoApiClient()
  const queryClient = useQueryClient()
  const {
    pbxId,
    onQueryStarted,
    onQuerySuccess,
    onQueryError,
    onQueryCancelled,
    options,
  } = params
  const [queryParams, setQueryParams] = useState<UseGetContactsQueryParams>()

  const getQueryKey = () => {
    const key = ['contacts', pbxId, { ...queryParams }]
    return key
  }
  const tryCancelQuery: QueryCancellation = () => {
    queryClient.cancelQueries({
      queryKey: getQueryKey(),
    })
  }

  const refetch = (params: UseGetContactsQueryParams) => {
    tryCancelQuery()
    setQueryParams(params)
  }

  const query = useQuery<PaginatedApiResult<Contact>, ApiError>({
    queryKey: getQueryKey(),
    queryFn: async ({ signal }) => {
      onQueryStarted?.()
      if (pbxId === 0) {
        onQueryError?.()
        return Promise.reject({ message: 'pbxId is required' })
      }

      const params: ContactsQueryParams = {
        pbx_id: pbxId,
        contact_source_ids: queryParams?.contactSourceIds?.join(','),
        search: queryParams?.search,
        offset: queryParams?.offset,
        limit: queryParams?.limit,
        ordering: getOrderingString(queryParams?.ordering),
      }

      const { ok, data, status } = await api.contacts.getContacts(
        params as Parameters<typeof api.contacts.getContacts>[0],
        { signal },
      )

      if (!ok) {
        const error = {
          ...(data as object),
          status,
        }
        if (!signal?.aborted) {
          onQueryCancelled?.()
        }
        onQueryError?.(error, params)
        return Promise.reject(error)
      }

      onQuerySuccess?.(data!)
      return data!
    },
    ...options,
    enabled: options?.enabled ?? false,
    retry: (options?.retry as number | boolean | undefined) ?? false,
  })

  useEffect(() => {
    if (queryParams) {
      query.refetch()
    }
  }, [queryParams])

  return {
    ...query,
    refetch,
    tryCancelQuery,
  }
}

interface UseGetContactParams {
  pbxId?: number
  contactId?: number | string
  onQuerySuccess?: (data: Contact) => void
  onQueryError?: (contactId: number | string, error?: ApiError) => void
  options?: Omit<UseQueryOptions<Contact, ApiError>, 'queryKey'>
}

/**
 * @description Get a contact by id - disabled by default - use refetch with params or set enabled to true in options
 * @param params
 * @returns
 */
export const useGetContact = (params: UseGetContactParams) => {
  const api = useMangoApiClient()
  const [queryParams, setQueryParams] = useState<
    { contactId: number | string } | undefined
  >(params?.contactId ? { contactId: params?.contactId ?? '' } : undefined)

  const { pbxId, onQuerySuccess, onQueryError, options } = params

  const contactId = queryParams?.contactId ?? 0
  const refetch = (contactId: number | string) => {
    setQueryParams({ contactId })
  }

  const query = useQuery<Contact, ApiError>({
    queryKey: ['contact', contactId, pbxId],
    queryFn: async () => {
      if (contactId === 0 || !pbxId) {
        return Promise.reject({ message: 'contactId and pbxId are required' })
      }

      const { ok, data, status } = await api.contacts.getContact(
        contactId,
        pbxId,
      )

      if (!ok) {
        const error = {
          ...(data as object),
          status,
        }
        onQueryError?.(contactId, error)
        return Promise.reject(error)
      }

      onQuerySuccess?.(data!)
      return data!
    },
    ...options,
    enabled: options?.enabled ?? false,
    retry: (options?.retry as number | boolean | undefined) ?? false,
  })

  useEffect(() => {
    if (queryParams?.contactId) {
      query.refetch()
    }
  }, [queryParams])

  return {
    ...query,
    refetch,
  }
}
